import { type ReactNode, useEffect, useState } from 'react';
import '../../../../css/flow/activate.less';
import { RUNTIME_URI } from '../../constants/platform';
import {
    type McrDeployResult,
    connectToEngineSocket,
    disconnectFromEngineSocket,
} from '../../mcr/signalRConnection';
import { activateFlow, buildFlow, deployFlow, snapshotFlow } from '../../sources/build';
import { getDevelopmentEnvironment } from '../../sources/environments';
import { getLatest } from '../../sources/flow';
import { deploy, notifyRuntimesOnDeploy } from '../../sources/releases';
import { getRuntimesForTenant } from '../../sources/runtime';
import translations from '../../translations';
import type { TenantRuntime } from '../../types/Tenant';
import type { Environment } from '../../types/environment';
import type { FlowResponseAPI } from '../../types/flow';
import type { CultureApi } from '../../types/translation';
import { EnvironmentsProvider } from '../admin/environments/EnvironmentsProvider';
import ButtonPrimary from '../buttons/ButtonPrimary';
import ErrorBoundary from '../generic/ErrorBoundary';
import FormGroup from '../generic/FormGroup';
import Modal from '../generic/modal/GenericModal';
import { useRun } from '../graph/RunProvider';
import Loader from '../loader/Loader';
import { useNotificationDispatch } from '../notifications/NotificationProvider';
import ActivateBuild from './ActivateBuild';
import ActivateFailed from './ActivateFailed';
import ActivatePublish from './ActivatePublish';
import ActivateRuntime from './ActivateRuntime';
import ActivateSuccess from './ActivateSuccess';
import ActivateSuccessRuntime from './ActivateSuccessRuntime';
import { isPublic } from '../../utils/flow';
import { useTenant } from '../TenantProvider';
import { registerPublishDisclaimerEvent } from '../../sources/auditLogs';
import { tryGetTraceId } from '../../utils/error';

interface Props {
    isAdmin: boolean;
    tenantHasOrganization: boolean;
    flowId: string;
    onClose: () => void;
    container?: HTMLElement | null;
    environmentsEnabled: boolean;
    themesEnabled: boolean;
    loadReleases: () => void;
}

type ViewState =
    | 'LOADING'
    | 'PUBLISH'
    | 'RUNTIME'
    | 'BUILD'
    | 'SUCCESS'
    | 'SUCCESS_RUNTIME'
    | 'FAILED'
    | 'PUBLISH_CONFIRM'
    | 'BUILD_CONFIRM'
    | 'RUNTIME_CONFIRM';

const Activate = ({
    isAdmin,
    tenantHasOrganization,
    flowId,
    onClose,
    container,
    environmentsEnabled,
    themesEnabled,
    loadReleases,
}: Props) => {
    const [error, setError] = useState<string | null>(null);
    const [errorTraceId, setErrorTraceId] = useState<string | null>(null);
    const [runtimes, setRuntimes] = useState<TenantRuntime[]>([]);
    const [selectedRuntime, setSelectedRuntime] = useState<TenantRuntime | undefined>(undefined);
    const [developmentEnvironment, setDevelopmentEnvironment] = useState<Environment | null>(null);
    const [comment, setComment] = useState<string | null>(null);
    const [releaseName, setReleaseName] = useState<string | null>(null);
    const [releaseId, setReleaseId] = useState<string | null>(null);
    const [viewState, setViewState] = useState<ViewState>('LOADING');
    const [culture, setCulture] = useState<CultureApi | undefined>(undefined);
    const [publishConfirmText, setPublishConfirmText] = useState<string>('');
    const [buildConfirmText, setBuildConfirmText] = useState<string>('');
    const tenant = useTenant();

    const { player, theme, setPlayer, setTheme, players, themes, getPlayers, getThemes } = useRun();

    const dispatchNotification = useNotificationDispatch();

    const requirePublicFlowPublishConfirmation =
        tenant.tenantSettings?.requirePublicFlowPublishConfirmation ?? true;

    const determineBuildView = async () => {
        setViewState('LOADING');
        const isPublic =
            ((await isFlowPublic(flowId)) && requirePublicFlowPublishConfirmation) ?? true;
        if (isPublic) {
            setViewState('BUILD_CONFIRM');
            return;
        }
        setViewState('BUILD');
    };

    const determinePublishView = async () => {
        setViewState('LOADING');

        const runtimes = await loadRuntimes();
        const isPublic =
            ((await isFlowPublic(flowId)) && requirePublicFlowPublishConfirmation) ?? true;

        // Check if runtimes are available first
        if (runtimes.length > 0) {
            if (isPublic) {
                setViewState('RUNTIME_CONFIRM');
            } else {
                setViewState('RUNTIME');
            }

            return;
        }

        // Determine publish view based on flow privacy
        if (isPublic) {
            setViewState('PUBLISH_CONFIRM');
        } else {
            setViewState('PUBLISH');
        }
    };

    // biome-ignore lint/correctness/useExhaustiveDependencies: Treat warnings as errors, fix later
    useEffect(() => {
        loadRuntimes();
        if (environmentsEnabled) {
            determineBuildView();
        } else {
            determinePublishView();
        }
    }, []);

    const loadRuntimes = async () => {
        // Only administrators have read permissions for /api/admin/1/tenant/runtimes
        if (isAdmin && tenantHasOrganization) {
            const runtimes = (await getRuntimesForTenant()) as TenantRuntime[];
            setRuntimes(runtimes);
            return runtimes;
        }

        return [];
    };

    const isFlowPublic = async (flowId: string) => {
        try {
            const flow = await getLatest(flowId);
            return isPublic(flow);
        } catch (e) {
            setError((e as Error).message);
            setErrorTraceId(tryGetTraceId(e));
            setViewState('FAILED');
        }

        return undefined;
    };

    const onRuntimeChanged = (runtime: TenantRuntime | undefined) => {
        setSelectedRuntime(runtime);
    };

    const onReleaseIdChanged = (id: string | null) => {
        setReleaseId(id);
    };

    const onReleaseNameChanged = (name: string | null) => {
        setReleaseName(name);
    };

    const onPublish = async () => {
        setViewState('LOADING');

        try {
            const snapshot = await snapshotFlow({
                flowId,
                comment,
            });

            const {
                id: { versionId },
            } = snapshot;

            if (selectedRuntime) {
                const messageId: string = crypto.randomUUID();
                const { connectionReady, result } = connectToEngineSocket();

                // Wait for connection to be ready before sending notification
                await connectionReady;

                await deployFlow(flowId, versionId, {
                    runtime: {
                        id: selectedRuntime.id,
                        isActive: true,
                        isDefault: true,
                        messageId,
                    },
                });

                try {
                    const deployResult = await result;

                    if (deployResult.messageId !== messageId) {
                        throw new Error('Returned Multi Cloud Runtime message ID does not match');
                    }
                } finally {
                    await disconnectFromEngineSocket();
                }
            } else {
                await activateFlow(flowId, versionId);
                setViewState('SUCCESS');
            }

            await getPlayers();
            await getThemes();
        } catch (e) {
            setError((e as Error).message);
            setErrorTraceId(tryGetTraceId(e));
            setViewState('FAILED');
        } finally {
            if (selectedRuntime) {
                setViewState('SUCCESS_RUNTIME');
            }
        }
    };

    const onBuild = async () => {
        setError(null);
        setErrorTraceId(null);
        setViewState('LOADING');

        const flowResponse: FlowResponseAPI = await buildFlow({
            releaseId,
            releaseName,
            flowId,
            comment,
        });

        await getPlayers();
        await getThemes();
        loadReleases();

        const devEnvironment = await getDevelopmentEnvironment();
        setDevelopmentEnvironment(devEnvironment);

        if (runtimes.length > 0) {
            const messageId: string = crypto.randomUUID();
            const { connectionReady, result } = connectToEngineSocket();

            // Wait for connection to be ready before sending notification
            await connectionReady;

            await notifyRuntimesOnDeploy(
                messageId,
                flowResponse.releaseId,
                devEnvironment.id,
                false,
            );

            let deployResult: McrDeployResult | null = null;
            try {
                deployResult = await result;

                if (deployResult.messageId !== messageId) {
                    throw new Error('Returned Multi Cloud Runtime message ID does not match');
                }
            } catch (error) {
                dispatchNotification({
                    type: 'notifyError',
                    payload: {
                        error,
                    },
                });
                setViewState('FAILED');
            } finally {
                if (deployResult?.success) {
                    setViewState('SUCCESS');
                }
                await disconnectFromEngineSocket();
            }
        } else {
            await deploy(flowResponse.releaseId, devEnvironment.id);
            setViewState('SUCCESS');
        }
    };

    let body: ReactNode;
    const footer = [
        <button className="btn btn-default" key="close" onClick={onClose} type="button">
            Close
        </button>,
    ];

    let url = '';

    if (environmentsEnabled && developmentEnvironment && !themesEnabled) {
        url = `${RUNTIME_URI}/${tenant.id}/play/${developmentEnvironment.defaultPlayerName}/?flow-id=${flowId}&environment-id=${developmentEnvironment.id}`;
    } else if (environmentsEnabled && themesEnabled && developmentEnvironment) {
        url = `${RUNTIME_URI}/${tenant.id}/play/theme/${developmentEnvironment.defaultThemeName}/?flow-id=${flowId}&environment-id=${developmentEnvironment.id}`;
    } else if (themesEnabled) {
        url = `${RUNTIME_URI}/${tenant.id}/play/theme/${theme}/?flow-id=${flowId}`;
    } else {
        url = `${RUNTIME_URI}/${tenant.id}/play/${player}/?flow-id=${flowId}`;
    }

    if (culture && !culture.isDefault) {
        // New cultures will have a null brand, country, language and variant, for these
        // we want to stick the code e.g. en-US in the url as its nicer
        if (culture?.brand || culture?.country || culture?.language || culture?.variant) {
            url += `&culture=${culture.id}`;
        } else {
            url += `&culture=${culture.code}`;
        }
    }

    switch (viewState) {
        case 'LOADING': {
            body = <Loader />;
            break;
        }

        case 'PUBLISH': {
            body = (
                <ErrorBoundary>
                    <ActivatePublish
                        comment={comment}
                        onCommentChanged={(e) => setComment(e.currentTarget.value)}
                    />
                </ErrorBoundary>
            );
            footer.push(
                <ButtonPrimary
                    onClick={onPublish}
                    key="publish"
                    name={translations.FLOW_publish_button}
                >
                    {translations.FLOW_publish_button}
                </ButtonPrimary>,
            );
            break;
        }

        case 'RUNTIME_CONFIRM':
        case 'PUBLISH_CONFIRM': {
            body = (
                <ErrorBoundary>
                    <div>
                        <p>{translations.FLOW_public_publish_confirmation}</p>
                        <p>{translations.FLOW_public_publish_warning_2}</p>
                        <FormGroup
                            label={translations.FLOW_public_confirm_label}
                            htmlFor="publishConfirmInput"
                        >
                            <input
                                autoComplete="off"
                                id="publishConfirmInput"
                                type="text"
                                value={publishConfirmText}
                                onChange={(e) => setPublishConfirmText(e.target.value)}
                                className="form-control"
                                placeholder="Type PUBLIC here"
                            />
                        </FormGroup>
                    </div>
                </ErrorBoundary>
            );
            footer.push(
                <ButtonPrimary
                    onClick={() => {
                        registerPublishDisclaimerEvent(flowId);
                        setViewState(viewState === 'PUBLISH_CONFIRM' ? 'PUBLISH' : 'RUNTIME');
                    }}
                    key="publish-confirm"
                    name="Publish Confirm"
                    disabled={publishConfirmText !== 'PUBLIC'}
                >
                    {translations.COMMON_confirm}
                </ButtonPrimary>,
            );
            break;
        }

        case 'BUILD_CONFIRM': {
            body = (
                <ErrorBoundary>
                    <div>
                        <p>{translations.FLOW_public_build_confirmation}</p>
                        <p>{translations.FLOW_public_build_warning_2}</p>
                        <FormGroup
                            label={translations.FLOW_public_confirm_label}
                            htmlFor="buildConfirmInput"
                        >
                            <input
                                autoComplete="off"
                                id="buildConfirmInput"
                                type="text"
                                value={buildConfirmText}
                                onChange={(e) => setBuildConfirmText(e.target.value)}
                                className="form-control"
                                placeholder="Type PUBLIC here"
                            />
                        </FormGroup>
                    </div>
                </ErrorBoundary>
            );
            footer.push(
                <ButtonPrimary
                    onClick={() => {
                        registerPublishDisclaimerEvent(flowId);
                        setViewState('BUILD');
                    }}
                    key="build-confirm"
                    name="Build Confirm"
                    disabled={buildConfirmText !== 'PUBLIC'}
                >
                    {translations.COMMON_confirm}
                </ButtonPrimary>,
            );
            break;
        }

        case 'RUNTIME': {
            body = (
                <ErrorBoundary>
                    <ActivateRuntime
                        runtimes={runtimes}
                        onChange={onRuntimeChanged}
                        comment={comment}
                        onCommentChanged={(e) => setComment(e.currentTarget.value)}
                    />
                </ErrorBoundary>
            );
            footer.push(
                <ButtonPrimary
                    onClick={onPublish}
                    key="publish"
                    name={translations.FLOW_publish_button}
                >
                    {translations.FLOW_publish_button}
                </ButtonPrimary>,
            );
            break;
        }

        case 'BUILD': {
            body = (
                <ErrorBoundary>
                    <EnvironmentsProvider>
                        <ActivateBuild
                            onCommentChanged={(e) => setComment(e.currentTarget.value)}
                            onReleaseIdChanged={onReleaseIdChanged}
                            onReleaseNameChanged={onReleaseNameChanged}
                            isThemeBuild={false}
                        />
                    </EnvironmentsProvider>
                </ErrorBoundary>
            );
            footer.push(
                <ButtonPrimary
                    onClick={onBuild}
                    disabled={!(releaseName || releaseId)}
                    key="build"
                    name="Build"
                >
                    {translations.ENVIRONMENT_build_flow}
                </ButtonPrimary>,
            );
            break;
        }

        case 'SUCCESS': {
            body = (
                <ErrorBoundary>
                    <ActivateSuccess
                        flowId={flowId}
                        url={url}
                        players={players}
                        selectedPlayer={player}
                        setSelectedPlayer={(e) => setPlayer(e.currentTarget.value)}
                        themes={themes}
                        selectedTheme={theme}
                        setSelectedTheme={(e) => setTheme(e.currentTarget.value)}
                        themesEnabled={themesEnabled}
                        environmentsEnabled={environmentsEnabled}
                        culture={culture}
                        onCultureChange={setCulture}
                    />
                </ErrorBoundary>
            );
            footer.push(
                <a
                    className="btn btn-primary"
                    key="run"
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    Run
                </a>,
            );
            break;
        }

        case 'SUCCESS_RUNTIME': {
            body = selectedRuntime ? (
                <ErrorBoundary>
                    <ActivateSuccessRuntime
                        flowId={flowId}
                        tenantId={tenant.id}
                        selectedRuntime={selectedRuntime}
                    />
                </ErrorBoundary>
            ) : null;
            break;
        }

        case 'FAILED': {
            body = (
                <ActivateFailed
                    error={error}
                    traceId={errorTraceId}
                    retry={environmentsEnabled ? onBuild : onPublish}
                    environmentsEnabled={environmentsEnabled}
                    isMCR={!!selectedRuntime}
                />
            );
            break;
        }
    }

    const bodyClassName =
        viewState === 'FAILED'
            ? 'activation-failed'
            : viewState === 'SUCCESS' || viewState === 'SUCCESS_RUNTIME'
              ? 'activation-successful'
              : viewState === 'PUBLISH_CONFIRM' || viewState === 'BUILD_CONFIRM'
                ? 'auto-height'
                : '';

    return (
        <Modal
            show
            onHide={onClose}
            container={container}
            className="config-modal"
            title={environmentsEnabled ? 'Build Flow' : 'Publish Flow'}
            headerClassName="pendo-publish-flow-modal-header"
            bodyClassName={bodyClassName}
            renderBody={() => <>{body}</>}
            renderFooter={() => <>{footer}</>}
        />
    );
};

export default Activate;
